import clsx from "clsx";

export const buttonVariants = ["primary", "secondary", "outlined"] as const;
type ButtonVariants = (typeof buttonVariants)[number];

export const buttonStates = ["default", "disabled", "loading"] as const;
type ButtonStates = (typeof buttonStates)[number];

export const buttonSizes = ["sm", "m", "l"] as const;
type ButtonSizes = (typeof buttonSizes)[number];

type ButtonProps = {
  variant?: ButtonVariants;
  state?: ButtonStates;
  size?: ButtonSizes;
  onClick?: () => void;
  leftComponent?: React.ReactNode;
  rightComponent?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
};

const disabled = "text-grey_total-100 bg-grey_total-100";

const containerButtonStyle = {
  primary: {
    default: "text-white bg-red_total hover:bg-red_total_darker",
    disabled,
    loading: "text-white bg-red_total",
  },
  secondary: {
    default: "text-white bg-grey_total hover:bg-black",
    disabled,
    loading: "text-white bg-grey_total",
  },
  outlined: {
    default:
      "text-red_total border-red_total bg-white border hover:text-red_total_darker hover:border-red_total_darker hover:bg-light_grey",
    disabled,
    loading: "text-red_total border-red_total bg-white border",
  },
} satisfies Record<ButtonVariants, Record<ButtonStates, string>>;

const shadowButtonStyle = {
  primary: {
    default: "via-red_total_darker",
    disabled: "",
    loading: "",
  },
  secondary: {
    default: "via-black",
    disabled: "",
    loading: "",
  },
  outlined: {
    default: "via-light_grey",
    disabled: "",
    loading: "",
  },
} satisfies Record<ButtonVariants, Record<ButtonStates, string>>;

const sizeButtonStyle = {
  sm: "text-xs h-[36px] py-sm px-s",
  m: "text-sm h-2xl py-s px-m",
  l: "text-base h-[60px] py-[18px] px-l",
} satisfies Record<ButtonSizes, string>;

const sizeComponentStyle = {
  sm: "size-m",
  m: "size-l",
  l: "size-l",
} satisfies Record<ButtonSizes, string>;

const Button = ({
  variant = "primary",
  state = "default",
  size = "sm",
  onClick,
  leftComponent,
  rightComponent,
  className,
  children,
  "data-testid": testId,
}: ButtonProps) => {
  const isDisabled = state === "disabled" || state === "loading";

  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      className={clsx(
        "group relative overflow-hidden whitespace-nowrap rounded-rounded font-bold outline-none transition-all duration-700",
        containerButtonStyle[variant][state],
        sizeButtonStyle[size],
        { "hover:shadow-2": !isDisabled },
        className
      )}
      data-testid={testId}
    >
      <div className="relative z-10 flex items-center justify-center gap-s">
        <>
          {leftComponent && (
            <div className="flex">
              <div className={sizeComponentStyle[size]}>{leftComponent}</div>
            </div>
          )}
        </>
        <span>{children}</span>
        {rightComponent && (
          <div className={sizeComponentStyle[size]}>{rightComponent}</div>
        )}
      </div>
      <span
        className={clsx(
          "absolute left-1/2 top-1/2 h-full w-0 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-transparent to-transparent opacity-5 transition-all duration-700 group-hover:h-full group-hover:w-[150%] group-hover:opacity-100",
          shadowButtonStyle[variant][state]
        )}
      />
    </button>
  );
};

export default Button;
